/**
 * ConfirmDialog - imperative confirmation modal
 *
 * Usage:
 *   import { confirm } from './ConfirmDialog';
 *   const confirmed = await confirm({ title: 'Delete?', message: 'Are you sure?' });
 *   if (confirmed) { ... }
 */

import { createRoot } from 'react-dom/client';
import type { ReactNode } from 'react';

interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface AlertOptions {
  title: string;
  message: ReactNode;
  buttonLabel?: string;
}

export function alert(options: AlertOptions): Promise<void> {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      root.unmount();
      container.remove();
    };

    const handleOk = () => {
      cleanup();
      resolve();
    };

    root.render(
      <div style={styles.overlay} onClick={handleOk}>
        <div style={styles.dialog} onClick={e => e.stopPropagation()}>
          <div style={styles.title}>{options.title}</div>
          <div style={styles.message}>{options.message}</div>
          <div style={styles.buttons}>
            <button onClick={handleOk} style={styles.okButton}>
              {options.buttonLabel ?? 'OK'}
            </button>
          </div>
        </div>
      </div>
    );
  });
}

export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      root.unmount();
      container.remove();
    };

    const handleConfirm = () => {
      cleanup();
      resolve(true);
    };

    const handleCancel = () => {
      cleanup();
      resolve(false);
    };

    root.render(
      <div style={styles.overlay} onClick={handleCancel}>
        <div style={styles.dialog} onClick={e => e.stopPropagation()}>
          <div style={styles.title}>{options.title}</div>
          <div style={styles.message}>{options.message}</div>
          <div style={styles.buttons}>
            <button onClick={handleCancel} style={styles.cancelButton}>
              {options.cancelLabel ?? 'Cancel'}
            </button>
            <button onClick={handleConfirm} style={styles.confirmButton}>
              {options.confirmLabel ?? 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  });
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  dialog: {
    background: '#1f2937',
    borderRadius: 8,
    padding: 24,
    minWidth: 300,
    maxWidth: 400,
    border: '1px solid #374151'
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: 'white',
    marginBottom: 12
  },
  message: {
    fontSize: 13,
    color: '#9ca3af',
    marginBottom: 20,
    lineHeight: 1.5
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8
  },
  cancelButton: {
    background: '#374151',
    color: 'white',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '8px 16px',
    fontSize: 13,
    cursor: 'pointer'
  },
  confirmButton: {
    background: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    fontSize: 13,
    cursor: 'pointer'
  },
  okButton: {
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    fontSize: 13,
    cursor: 'pointer'
  }
};
